import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { Alert, Platform } from 'react-native';
import type { AlertButton, AlertOptions } from 'react-native';
import { CrossPlatformAlert } from '../components/CrossPlatformAlert';
import { BillingModal } from '../components/billing';
import type { BillingModalConfig } from '../components/billing';

type AlertRequest = {
  title: string;
  message?: string;
  buttons?: AlertButton[];
};

type AlertContextType = {
  alert: (title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) => void;
  showBillingModal: (config: BillingModalConfig) => void;
  dismissBillingModal: () => void;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

/**
 * AlertProvider - Cross-platform alert system
 *
 * Provides a unified `alert()` API that works across iOS, Android, and Web:
 * - On iOS/Android: Uses native Alert.alert for the native look and feel
 * - On Web: Uses a custom modal component (CrossPlatformAlert)
 *
 * Also provides `showBillingModal()` for rich billing dialogs that render
 * the same BillingModal component on all platforms.
 *
 * Usage:
 * ```tsx
 * const { alert, showBillingModal } = useAlert();
 *
 * // Simple alert (existing behavior)
 * alert('Title', 'Message', [
 *   { text: 'Cancel', style: 'cancel' },
 *   { text: 'Confirm', onPress: () => {...} }
 * ]);
 *
 * // Rich billing modal (new)
 * showBillingModal({
 *   title: 'Before you go...',
 *   description: "We'd hate to see you leave!",
 *   buttons: [
 *     { text: 'Keep my plan', variant: 'primary', onPress: () => {...} },
 *     { text: 'Cancel subscription', variant: 'destructive', onPress: () => {...} },
 *   ],
 * });
 * ```
 */
export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [alertRequest, setAlertRequest] = useState<AlertRequest | null>(null);
  const [billingConfig, setBillingConfig] = useState<BillingModalConfig | null>(null);

  const alert = useCallback((
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: AlertOptions
  ) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      // Use native Alert on mobile
      Alert.alert(title, message, buttons, options);
    } else {
      // Use custom modal on web
      setAlertRequest({
        title,
        message: message || '',
        buttons: buttons || [{ text: 'OK' }], // Default button if none provided
      });
    }
  }, []);

  const showBillingModal = useCallback((config: BillingModalConfig) => {
    setBillingConfig(config);
  }, []);

  const dismissBillingModal = useCallback(() => {
    setBillingConfig(null);
  }, []);

  const handleClose = () => {
    setAlertRequest(null);
  };

  return (
    <AlertContext.Provider value={{ alert, showBillingModal, dismissBillingModal }}>
      {children}
      {alertRequest && (
        <CrossPlatformAlert
          visible={!!alertRequest}
          title={alertRequest.title}
          message={alertRequest.message || ''}
          buttons={alertRequest.buttons || []}
          onClose={handleClose}
        />
      )}
      {billingConfig && (
        <BillingModal
          visible={!!billingConfig}
          config={billingConfig}
          onClose={dismissBillingModal}
        />
      )}
    </AlertContext.Provider>
  );
};

/**
 * useAlert - Hook to show cross-platform alerts
 *
 * Must be used within an AlertProvider.
 *
 * @returns {alert} Function with same signature as Alert.alert
 * @returns {showBillingModal} Function to show rich billing modals
 * @returns {dismissBillingModal} Function to programmatically dismiss billing modal
 */
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
